import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, concatMap, map, of, take, tap, throwError, timeout } from 'rxjs';

import { LanguageInterceptor, Logger, ProblemJsonInterceptor, RwcInterceptor } from '@mobi/rwc-utils-common-ng-jslib';

import { ApiGatewayRoutingInterceptor } from '../api-gateway-routing.interceptor';

const INTERCEPTOR_TIMEOUT_MS = 10_000;

export function globalInterceptor(): HttpInterceptorFn {
    return (req, next) => {
        function interceptRequest(
            interceptor: RwcInterceptor & {
                interceptRequest(req: HttpRequest<unknown>): Observable<HttpRequest<unknown>>;
            },
            request: HttpRequest<unknown>
        ): Observable<HttpRequest<unknown>> {
            return interceptWithTimeout(
                interceptor,
                request,
                () => interceptor.interceptRequest(request),
                'Request',
                request
            );
        }

        function interceptError(
            interceptor: RwcInterceptor & {
                interceptResponseError(req: HttpRequest<unknown>, err: HttpErrorResponse): Observable<unknown>;
            },
            request: HttpRequest<unknown>,
            errorResponse: HttpErrorResponse
        ): Observable<unknown> {
            return interceptWithTimeout(
                interceptor,
                request,
                () => interceptor.interceptResponseError(request, errorResponse),
                'ErrorResponse',
                errorResponse
            );
        }

        function interceptWithTimeout<T>(
            interceptor: RwcInterceptor,
            request: HttpRequest<unknown>,
            invokeFn: () => Observable<unknown>,
            logContextName: string,
            defaultValue: T
        ): Observable<any> {
            const name = interceptor.constructor.name;
            if (!interceptor.appliesToRequest || interceptor.appliesToRequest(request)) {
                logger.trace(`[${name}]: Start intercepting ${logContextName} ${request.url}`);
                const start = performance.now();

                return invokeFn().pipe(
                    take(1),
                    timeout({
                        first: INTERCEPTOR_TIMEOUT_MS,
                        with: () => {
                            const exception = new Error(
                                `[${name}]: Timeout occurred while intercepting ${logContextName} ${request.url}`
                            );
                            logger.error(exception);
                            // eslint-disable-next-line sonarjs/no-nested-functions
                            return throwError(() => exception);
                        }
                    }),
                    tap({
                        next: req =>
                            logger.trace(
                                `[${name}]: Finished intercepting ${logContextName} ${(req as { url: string }).url} took ${
                                    performance.now() - start
                                }ms`
                            )
                    })
                );
            }
            logger.trace(`[${name}]: Interceptor does not apply to ${logContextName} ${request.url}`);
            return of(defaultValue);
        }

        const apiGatewayRoutingInterceptor = inject(ApiGatewayRoutingInterceptor);
        const problemJsonInterceptor = inject(ProblemJsonInterceptor);
        const languageInterceptor = inject(LanguageInterceptor);
        const logger = inject(Logger).createCustomLogger('GlobalInterceptor');

        return of(req)
            .pipe(
                // request interceptors
                concatMap((req: HttpRequest<unknown>) => interceptRequest(apiGatewayRoutingInterceptor, req)),
                concatMap((req: HttpRequest<unknown>) => interceptRequest(problemJsonInterceptor, req)),
                concatMap((req: HttpRequest<unknown>) => interceptRequest(languageInterceptor, req))
            )
            .pipe(
                // request
                concatMap((request: HttpRequest<unknown>) =>
                    next(request).pipe(map((response: HttpEvent<unknown>) => ({ request, response })))
                )
            )
            .pipe(
                map(({ response }) => response),
                // error handlers
                catchError(errorResponse => {
                    if (errorResponse instanceof HttpErrorResponse) {
                        return interceptError(problemJsonInterceptor, req, errorResponse);
                    }
                    return throwError(() => errorResponse);
                })
            ) as any;
    };
}
